import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Case, CaseState, Reviewer, ReviewStep } from '../../../domain/types/ahr';
import { AHRService, Result } from '../../../domain';
import { AdHocCommitteeInfo, AdHocView } from '../../../domain/data-access-interfaces';
import * as committeeHelpers from '../committee-helpers';

/**
 * The Review Steps row of the AHR case modal: the numbered list of steps a
 * case goes through, with the review writer in bold. PRC and Ad Hoc steps
 * offer inline editing; everything else is read-only display.
 */
@Component({
	selector: 'review-steps-editor',
	templateUrl: './review-steps-editor.component.html'
})
export class ReviewStepsEditorComponent implements OnChanges {
	@Input() case: Case;
	@Output() changed = new EventEmitter<void>();

	editingPrc = false;
	editingAdHoc = false;
	adHocView: AdHocView | null = null;
	/** The committee this candidate is assigned to; null before the roster loads or when unassigned. */
	adHocCommittee: AdHocCommitteeInfo | null = null;
	private lastCaseId: string | null = null;

	// Template-visible re-exports of the shared helpers.
	adHocChair = committeeHelpers.adHocChair;
	adHocCommitteeLabel = committeeHelpers.adHocCommitteeLabel;

	constructor(
		private readonly svc: AHRService,
		private readonly dialog: MatDialog,
		private readonly snackbar: MatSnackBar
	) { }

	ngOnChanges(): void {
		// Leave edit mode when the modal navigates to a different case, or when
		// this one stops being editable — otherwise destructive controls leak onto
		// cases the pencil was never clicked for. A same-case refresh after an
		// edit keeps the editor open for follow-up edits.
		const caseChanged = !this.case || this.case.id !== this.lastCaseId;
		if (caseChanged || !this.editable) {
			this.editingPrc = false;
			this.editingAdHoc = false;
		}
		if (caseChanged) {
			// Another case's roster (and committee) must not linger on screen;
			// within the same case it stays visible during a re-fetch to avoid a
			// rendering flash on every modal refresh.
			this.adHocView = null;
			this.adHocCommittee = null;
		}
		this.lastCaseId = this.case ? this.case.id : null;

		// The case payload has no committee id, so the Ad Hoc step's display comes
		// from the year's roster. Re-fetch whenever the modal re-fetches the case
		// (each edit produces a new Case reference, so this also refreshes after edits).
		if (this.case && this.case.reviewSteps.some(s => this.stepKind(s) === 'adhoc')) {
			// Guard against out-of-order responses when navigating between cases:
			// only the roster matching the currently displayed period may land.
			const yearId = this.case.yearId;
			this.svc.getAdHocCommittees(yearId)
				.then(v => {
					if (this.case && this.case.yearId === yearId) {
						this.adHocView = v;
						this.adHocCommittee = v.committees
							.find(c => c.candidates.some(cand => cand.userId === this.case.userId)) || null;
					}
				})
				.catch(() => this.snackbar.open('Could not load the ad hoc committee roster', undefined, { duration: 5000 }));
		} else {
			this.adHocView = null;
			this.adHocCommittee = null;
		}
	}

	// Assignment edits only make sense while the case can still (re)enter review.
	get editable(): boolean {
		const s = this.case && this.case.state;
		return s === CaseState.ReadyToSendToCandidate
			|| s === CaseState.WithCandidate
			|| s === CaseState.InReview
			|| s === CaseState.Stalled;
	}

	stepKind(s: ReviewStep): 'prc' | 'adhoc' | 'other' {
		return s.id === 'PeerReviewCommittee' ? 'prc'
			: s.id === 'AdHocCommittee' ? 'adhoc'
			: 'other';
	}

	/** The first reviewer added to an empty step becomes the writer (same rule as the Committees page). */
	addPrcReviewer(s: ReviewStep): void {
		const writesReview = this.reviewers(s.id).length === 0;
		committeeHelpers.pickPerson(this.dialog, userId =>
			this.apply(this.svc.upsertPrcReviewer(this.case.yearId, this.case.userId, userId, writesReview)),
			this.reviewers(s.id).map(r => r.userId), 'Add a Reviewer');
	}

	makePrcWriter(r: Reviewer): void {
		this.apply(this.svc.upsertPrcReviewer(this.case.yearId, this.case.userId, r.userId, true));
	}

	removePrcReviewer(r: Reviewer): void {
		this.apply(this.svc.removePrcReviewer(this.case.yearId, this.case.userId, r.userId));
	}

	/** The <select> is reset immediately: on success the roster reloads anyway, and
	 *  on failure the stale choice must not linger as if it took effect. */
	onAdHocSelect(sel: HTMLSelectElement): void {
		const v = sel.value;
		sel.selectedIndex = 0;
		if (!v) return;
		if (v === 'new') {
			this.svc.createAdHocCommittee(this.case.yearId)
				.then(committeeId =>
					this.svc.assignAdHocCandidate(this.case.yearId, committeeId, this.case.userId)
						.then(result => result.matchDo(
							() => this.changed.emit(),
							errorMsg => {
								// A committee created for an assignment that failed would sit
								// empty and unexplained on the committees page — clean it up.
								this.svc.deleteAdHocCommittee(this.case.yearId, committeeId);
								this.snackbar.open(errorMsg, undefined, { duration: 5000 });
							})))
				.catch(() => this.snackbar.open('Failed to create committee.', undefined, { duration: 5000 }));
		} else if (v === 'unassign') {
			const c = this.adHocCommittee;
			if (c) this.apply(this.svc.unassignAdHocCandidate(this.case.yearId, c.committeeId, this.case.userId));
		} else {
			this.apply(this.svc.assignAdHocCandidate(this.case.yearId, +v, this.case.userId));
		}
	}

	/** Emit changed on success (the modal re-fetches the case), snack the error otherwise. */
	private apply(op: Promise<Result<void, string>>): void {
		committeeHelpers.applyResult(op, this.snackbar, () => this.changed.emit());
	}

	// Resolves the "(s)" suffix in a review-step name (e.g. "Supervisor(s)") to the singular or
	// plural form based on how many reviewers the step has. Names without "(s)" are unchanged.
	stepLabel(shortName: string, reviewerCount: number): string {
		if (shortName && shortName.indexOf('(s)') >= 0) {
			// 0 or 1 reviewers → singular; only pluralize for 2+.
			return shortName.replace('(s)', reviewerCount <= 1 ? '' : 's');
		}
		return shortName;
	}

	reviewers(stepId: string): Reviewer[] {
		return this.case.reviewersByStepId.get(stepId) || [];
	}
}
